/* __next_internal_client_entry_do_not_use__ TrustpilotReviews,Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import useTrustpilotReviews from 'lib/hooks/trustpilot/useTrustpilotReviews';
import { tv } from 'tailwind-variants';
import formatDate from 'lib/utils/date-formatter';
import TrustpilotSkeleton from './TrustpilotSkeleton';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { TRUSTPILOT_CONSTANTS } from 'helpers/Constants/SiteConfig';
import { getStarFillColor, getRatingLabel } from 'lib/trustpilot/utility';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const EMPTY_STAR_COLOR = '#dcdce6';
const TrustpilotStarSvg = ({ filled, fillColor })=>/*#__PURE__*/ _jsx("div", {
        className: "w-[24px] h-[24px] flex items-center justify-center",
        style: {
            backgroundColor: filled ? fillColor : EMPTY_STAR_COLOR
        },
        children: /*#__PURE__*/ _jsx("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            width: "17",
            height: "16",
            viewBox: "0 0 17 16",
            "aria-hidden": "true",
            children: /*#__PURE__*/ _jsx("path", {
                d: "M8.4 12.125L12.05 11.2L13.575 15.9L8.4 12.125ZM16.8 6.05H10.375L8.4 0L6.425 6.05H0L5.2 9.8L3.225 15.85L8.425 12.1L11.625 9.8L16.8 6.05Z",
                fill: "white"
            })
        })
    });
const StarRating = ({ rating })=>{
    const fillColor = getStarFillColor(rating);
    return /*#__PURE__*/ _jsx("div", {
        className: "flex gap-[4px]",
        role: "img",
        "aria-label": `${rating} out of 5 stars`,
        children: [
            1,
            2,
            3,
            4,
            5
        ].map((star)=>/*#__PURE__*/ _jsx(TrustpilotStarSvg, {
                filled: star <= Math.round(rating),
                fillColor: fillColor
            }, star))
    });
};
export const TrustpilotReviews = (props)=>{
    const { fields: rawFields, params } = props || {};
    const { removeBottomPadding } = props?.fields || {};
    const fields = rawFields ?? {};
    const { sitecoreContext } = useSitecoreContext();
    const businessUnitId = TRUSTPILOT_CONSTANTS.BUSINESS_UNIT_ID;
    const maxReviews = fields.numberOfReviews?.value ?? TRUSTPILOT_CONSTANTS.DEFAULT_MAX_REVIEWS;
    const language = TRUSTPILOT_CONSTANTS.DEFAULT_LANGUAGE;
    const noPaddingBottom = Boolean(removeBottomPadding?.value);
    const routeFields = sitecoreContext?.route?.fields;
    const serviceTag = routeFields?.tag?.value ?? '';
    const { cta, hideOverallRating, minStars, reviewCharacterLimit, title, logoLink, allReviewsLink, hideTrustpilotBadgeWithText, hideTrustpilotLogo } = fields;
    const { getDictionaryValue } = useDictionary();
    const styles = parseStyleParams(params, [
        'cta1'
    ]);
    const isEditing = useIsEditing();
    const theme = props?.params?.selectTheme;
    const isSmallContainer = params?.smallContainer === '1';
    const hasServiceTag = Boolean(serviceTag);
    const { data, isLoading } = useTrustpilotReviews({
        businessUnitId,
        maxReviews,
        minStars: minStars?.value,
        serviceTag,
        language,
        includeSummary: !hideOverallRating?.value
    });
    const reviews = data?.reviews ?? [];
    const summary = data?.summary ?? null;
    if (!hasServiceTag && !isEditing) return /*#__PURE__*/ _jsx(_Fragment, {});
    if (isLoading) return /*#__PURE__*/ _jsx(TrustpilotSkeleton, {});
    if (!isEditing && !reviews.length) return /*#__PURE__*/ _jsx(_Fragment, {});
    const { sectionWrapper, base, inner, eyebrowWrapper, eyebrowText, eyebrowImage, summaryBar, summaryExcellent, summaryCount, summaryCountText, cardsLayout, summaryContentText, card, reviewText, userBlock, userName, userDate, ctaWrapper, ctaButton, sectionTitleWrapper, sectionTitle, sectionDivider } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("div", {
        "data-component": "authorable/shared/content/TrustpilotReviews",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-trustpilot-reviews-${props?.rendering?.uid}`),
        className: sectionWrapper({
            isSmallContainer
        }),
        children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsx("div", {
                className: base({
                    removeBottomPadding: noPaddingBottom
                }),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: inner(),
                    children: [
                        !hideTrustpilotBadgeWithText?.value && /*#__PURE__*/ _jsxs("div", {
                            className: eyebrowWrapper(),
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: eyebrowText(),
                                    children: getDictionaryValue('IndependentlyReviewedOn')
                                }),
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    field: logoLink,
                                    suppressNewTabIcon: true,
                                    ctaComponentClass: "default",
                                    className: eyebrowImage(),
                                    children: theme === 'ThemesDark' ? /*#__PURE__*/ _jsx(ImageWrapper, {
                                        field: {
                                            value: {
                                                src: `${process.env.PUBLIC_URL}/assets/trustpilot-logo-white.svg`,
                                                width: 147,
                                                height: 36
                                            }
                                        }
                                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                                        field: {
                                            value: {
                                                src: `${process.env.PUBLIC_URL}/assets/trustpilot-logo-black.svg`,
                                                width: 147,
                                                height: 36
                                            }
                                        }
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: sectionTitleWrapper(),
                            children: [
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: sectionTitle(),
                                    field: title,
                                    tag: "h2"
                                }),
                                title?.value ? /*#__PURE__*/ _jsx("span", {
                                    className: sectionDivider(),
                                    "data-alignment": "center"
                                }) : null
                            ]
                        }),
                        !hideOverallRating?.value && summary && /*#__PURE__*/ _jsxs("div", {
                            className: summaryBar(),
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: summaryExcellent(),
                                    children: getRatingLabel(summary.score.trustScore, getDictionaryValue)
                                }),
                                /*#__PURE__*/ _jsx(StarRating, {
                                    rating: summary?.score.trustScore
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: summaryCount(),
                                    children: /*#__PURE__*/ _jsxs(LinkWrapper, {
                                        field: allReviewsLink,
                                        suppressNewTabIcon: true,
                                        ctaComponentClass: "default",
                                        className: summaryCount(),
                                        children: [
                                            /*#__PURE__*/ _jsx("strong", {
                                                children: `${summary.numberOfReviews.total.toLocaleString()} `
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: summaryCountText(),
                                                children: getDictionaryValue('TrustpilotReviewsOn')
                                            }),
                                            !hideTrustpilotLogo?.value && /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: summaryContentText(),
                                                        children: "on"
                                                    }),
                                                    theme === 'ThemesDark' ? /*#__PURE__*/ _jsx(ImageWrapper, {
                                                        field: {
                                                            value: {
                                                                src: `${process.env.PUBLIC_URL}/assets/trustpilot-logo-small-white.svg`,
                                                                width: 97,
                                                                height: 36
                                                            }
                                                        }
                                                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                                                        field: {
                                                            value: {
                                                                src: `${process.env.PUBLIC_URL}/assets/trustpilot-logo-small-black.svg`,
                                                                width: 97,
                                                                height: 36
                                                            }
                                                        }
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: cardsLayout(),
                            children: reviews.slice(0, 3)?.map?.((review)=>{
                                const reviewCharLimit = reviewCharacterLimit?.value ?? TRUSTPILOT_CONSTANTS.DEFAULT_REVIEW_CHARACTER_LIMIT;
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: card(),
                                    children: [
                                        /*#__PURE__*/ _jsx(StarRating, {
                                            rating: review.stars
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: reviewText(),
                                            children: /*#__PURE__*/ _jsxs(LinkWrapper, {
                                                field: {
                                                    value: {
                                                        href: `https://uk.trustpilot.com/reviews/${review.id}`,
                                                        target: '_blank'
                                                    }
                                                },
                                                suppressNewTabIcon: true,
                                                ctaComponentClass: "default",
                                                className: reviewText(),
                                                children: [
                                                    review.text.substring(0, reviewCharLimit),
                                                    review.text.length > reviewCharLimit && '...'
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: userBlock(),
                                            children: [
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: userName(),
                                                    children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                                        field: {
                                                            value: {
                                                                href: `https://uk.trustpilot.com/reviews/${review.id}`,
                                                                target: '_blank'
                                                            }
                                                        },
                                                        suppressNewTabIcon: true,
                                                        ctaComponentClass: "default",
                                                        className: userName(),
                                                        children: review.consumer.displayName
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: userDate(),
                                                    children: formatDate(review.createdAt)
                                                })
                                            ]
                                        })
                                    ]
                                }, review.id);
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: ctaWrapper(),
                            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                field: cta,
                                className: ctaButton(),
                                ctaStyle: getCtaStyle(styles.cta1, 'secondary'),
                                ctaComponentClass: "default",
                                viewBox: "0 0 18 18",
                                remixIcon: "external-link-line",
                                remixIconSize: "m",
                                suppressNewTabIcon: true
                            })
                        })
                    ]
                })
            })
        })
    });
};
// No Sitecore datasource — component self-fetches from Trustpilot API
export const Default = withStandardComponentWrapper(TrustpilotReviews, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        sectionWrapper: [
            'scroll-mt-[64px]',
            'md:scroll-mt-0'
        ],
        base: [
            'w-full',
            'bg-component-section-bg',
            'relative',
            'before:z-[-1]',
            'before:h-full',
            'before:absolute',
            'before:bg-component-section-bg',
            'before:top-0',
            'before:left-[calc(-50vw+50%)]',
            'before:right-[calc(-50vw+50%)]',
            'py-component-section-container-padding-y'
        ],
        inner: [
            'flex',
            'flex-col',
            'items-center',
            'gap-component-section-container-padding-y',
            'lg:gap-component-section-container-gutter',
            'w-full'
        ],
        sectionTitleWrapper: [
            'flex',
            'flex-col',
            'gap-space-between-base',
            'max-w-columns-two-third-max-width',
            'justify-center',
            'mx-auto'
        ],
        sectionTitle: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge',
            'text-center'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider',
            'mx-auto'
        ],
        summaryBar: [
            'flex',
            'flex-wrap',
            'items-center',
            'justify-center',
            'gap-spacing-spacing-16'
        ],
        summaryExcellent: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body-bold',
            'text-typography-font-size-body-medium',
            'text-component-section-title'
        ],
        summaryCount: [
            '!font-typography-font-weight-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-component-section-title',
            'hover:text-component-section-title',
            '!p-0',
            '!gap-0'
        ],
        summaryCountText: [
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-section-title',
            'pl-[4px]'
        ],
        summaryContentText: [
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-section-title',
            'pr-[4px]',
            'pl-[4px]'
        ],
        eyebrowWrapper: [
            'flex',
            'items-end',
            'gap-space-between-tight'
        ],
        eyebrowText: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-section-title'
        ],
        eyebrowImage: [
            '!p-0'
        ],
        cardsLayout: [
            'flex',
            'flex-col',
            'lg:flex-row',
            'gap-layout-base-gutter',
            'w-full'
        ],
        card: [
            'bg-[#FDFDFD]',
            '[box-shadow:0_0_8px_0_var(--color-black-alpha-10,rgba(0,0,0,0.10))]',
            'rounded-general-border-radius-card',
            'p-padding-base',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'flex-1',
            'text-left'
        ],
        reviewText: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large',
            'font-typography-font-weight-body',
            'text-color-core-color-1-Navy',
            'flex-1',
            '!p-0'
        ],
        userBlock: [
            'flex',
            'flex-col',
            'gap-[2px]'
        ],
        userName: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'font-typography-font-weight-body-bold',
            'text-color-core-color-1-Navy',
            '!p-0'
        ],
        userDate: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'leading-[1.5]',
            'font-typography-font-weight-body',
            'text-color-grayscale-600'
        ],
        ctaWrapper: [
            'flex',
            'justify-center',
            'w-full'
        ],
        ctaButton: []
    },
    variants: {
        removeBottomPadding: {
            true: {
                base: [
                    '!pb-0'
                ]
            },
            false: {
                base: [
                    ''
                ]
            }
        },
        isSmallContainer: {
            true: {
                sectionWrapper: [
                    'contentWrapper'
                ]
            },
            false: {
                sectionWrapper: [
                    ''
                ]
            }
        }
    },
    defaultVariants: {
        removeBottomPadding: false,
        isSmallContainer: false
    }
});
